import { useEffect, useMemo, useRef } from 'react';
import Editor, { loader, OnMount } from '@monaco-editor/react';
import type { KnowledgeDocument } from '../../api';
import { BinaryDocumentPreview } from './BinaryDocumentPreview';
import { useKnowledgeBuilder } from './KnowledgeBuilderProvider';
import { useKnowledgeBuilderColors } from '../../theme/knowledgeBuilderColors';

loader.config({ paths: { vs: '/monaco/vs' } });

export const MonacoDocumentEditor = ({
  document,
}: {
  document: KnowledgeDocument;
}) => {
  const { saveDocument, session, updateDocument } = useKnowledgeBuilder();
  const colors = useKnowledgeBuilderColors();
  const saveTimer = useRef<ReturnType<typeof setTimeout>>();
  const editorRef = useRef<Parameters<OnMount>[0]>();
  const isBinaryAsset = /\.(png|pdf|docx?|xlsx?)$/i.test(document.path);
  const uri = useMemo(
    () =>
      `rdap-knowledge://${encodeURIComponent(
        session?.id ?? document.sessionId,
      )}/${encodeURIComponent(document.path)}`,
    [document.path, document.sessionId, session?.id],
  );

  // `saveDocument` gets a new identity on every keystroke (it closes over
  // `documents`, which changes on every edit). Reading it through a ref
  // keeps that churn out of the effect below's dependencies — otherwise the
  // effect re-runs on every keystroke and its cleanup fires the "flush"
  // path below immediately, short-circuiting the 900ms debounce and firing
  // a save on every character typed.
  const saveDocumentRef = useRef(saveDocument);
  saveDocumentRef.current = saveDocument;
  const documentPathRef = useRef(document.path);
  documentPathRef.current = document.path;

  useEffect(() => {
    return () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveDocumentRef.current(document.path);
      }
    };
  }, [document.path]);

  // Only push content into the live model on genuinely external updates —
  // switching documents, or a server round-trip that bumps `revision`
  // (save, AI proposal, revert). Local typing never changes `revision`, so
  // this never fires mid-edit; reacting to `document.content` directly was
  // racy (a single user action can emit more than one onChange) and could
  // overwrite the model with a stale value, resetting the cursor.
  useEffect(() => {
    const editor = editorRef.current;
    if (editor && document.content !== editor.getValue()) {
      editor.setValue(document.content);
    }
  }, [document.path, document.revision]);

  if (isBinaryAsset) {
    return <BinaryDocumentPreview document={document} />;
  }

  const onMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;
    const model = monaco.editor.getModels().find(item => item.uri.toString() === uri);
    if (model) {
      monaco.editor.setModelLanguage(model, languageForPath(document.path));
    }
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = undefined;
      }
      saveDocumentRef.current(documentPathRef.current);
    });
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, minHeight: 0 }}>
        <Editor
          path={uri}
          defaultValue={document.content}
          language={languageForPath(document.path)}
          theme={colors.monacoTheme}
          options={{
            minimap: { enabled: true, scale: 1, showSlider: 'mouseover' },
            wordWrap: 'on',
            scrollBeyondLastLine: false,
            fontSize: 13,
            fontFamily:
              '"SF Mono", Menlo, Monaco, Consolas, "Liberation Mono", monospace',
            lineHeight: 20,
            automaticLayout: true,
            bracketPairColorization: { enabled: true },
            cursorBlinking: 'smooth',
            formatOnPaste: true,
            formatOnType: true,
            glyphMargin: true,
            lineNumbersMinChars: 4,
            padding: { top: 12, bottom: 12 },
            renderLineHighlight: 'all',
            roundedSelection: false,
            scrollbar: {
              verticalScrollbarSize: 12,
              horizontalScrollbarSize: 12,
            },
            smoothScrolling: true,
            stickyScroll: { enabled: true },
          }}
          onMount={onMount}
          onChange={value => {
            updateDocument(document.path, value ?? '');
            if (saveTimer.current) {
              clearTimeout(saveTimer.current);
            }
            saveTimer.current = setTimeout(() => {
              saveDocumentRef.current(documentPathRef.current);
            }, 900);
          }}
        />
      </div>
    </div>
  );
};

export function languageForPath(path: string) {
  if (/\.md$/i.test(path)) {
    return 'markdown';
  }
  if (/\.ya?ml$/i.test(path)) {
    return 'yaml';
  }
  if (/\.json$/i.test(path)) {
    return 'json';
  }
  if (/\.css$/i.test(path)) {
    return 'css';
  }
  if (/\.tsx$/i.test(path)) {
    return 'typescript';
  }
  if (/\.(jsx|js|mjs|cjs)$/i.test(path)) {
    return 'javascript';
  }
  if (/\.html?$/i.test(path)) {
    return 'html';
  }
  if (/\.sh$/i.test(path)) {
    return 'shell';
  }
  return 'plaintext';
}
